import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import { configFloorSchema, configFloorPatchSchema } from '../../interfaces/IConfigFloor';

// ============================================================
// Param Schemas
// ============================================================

const projectFloorParamsSchema = z.object({
	projectId: requiredStringSchema,
	floorId: requiredStringSchema.describe('Floor ID'),
});

// ============================================================
// Query Schemas
// ============================================================

/**
 * `floorIds` replaces the old `GET /floor/list/:floorsIds`, which put a CSV in a
 * path segment — precisely the shape the convention forbids. It is a filter on
 * the collection, not a sub-resource, so it belongs in the query. Absent means
 * every floor of the project.
 */
const floorsQuerySchema = z.object({
	floorIds: requiredStringSchema.describe('Comma-separated floor IDs').optional(),
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * `floorId` moves to the path and `projectId` is gone: this used to be a
 * collection-level PUT that picked its target out of the body. It is built from
 * the defaults-free base — `configFloorSchema.partial()` still applies
 * `.default(false)` to every flag the caller omitted, so the old body silently
 * reset `isHoist`, `isSpecialFloor`, `isMovingFloor` and `isGroundFloor` on any
 * partial update.
 */
const updateFloorBodySchema = configFloorPatchSchema;

/**
 * Elements carry no `projectId`. They used to, and an array body exposes no
 * top-level key to the membership guard — which is why this route was marked
 * GUARD-BLIND. The path value is now the only project involved.
 */
const insertFloorsBodySchema = z.array(configFloorSchema.omit({ projectId: true }));

const floorFileSchema = z.file().mime(['image/jpeg']);

const uploadFloorImagesBodySchema = z.object({
	file: z.preprocess((val) => (Array.isArray(val) ? val : [val]), z.array(floorFileSchema)),
});

const deleteFloorsByIdsBodySchema = z.object({
	ids: z.array(z.string()),
});

// ============================================================
// Contract
// ============================================================

/**
 * Floors are project data: `/projects/:projectId/floors`.
 *
 * `{ root: true }` — every route here is project-scoped, so nothing is left
 * behind by dropping the `/floor` prefix.
 *
 * IMPORTANT: literal sub-paths (`/complete`, `/from-images`, `/byFloorIds`)
 * must be declared BEFORE the parameterized `/:floorId`.
 */
export const floorContract = defineContract(
	'floor',
	{
		getFloors: route({
			method: 'GET',
			path: '/projects/:projectId/floors',
			params: projectIdParamsSchema,
			query: floorsQuerySchema,
			response: z.array(configFloorSchema),
			operationId: 'getFloors',
			tags: ['Floor'],
			summary: 'List the floors of a project, optionally filtered by floorIds',
		}),
		getFloorsWithCompleteData: route({
			method: 'GET',
			path: '/projects/:projectId/floors/complete',
			params: projectIdParamsSchema,
			response: z.any(),
			operationId: 'getFloorsWithCompleteData',
			tags: ['Floor'],
			summary: 'List the floors of a project with their areas, points and anchors',
		}),
		insertFloors: route({
			method: 'POST',
			path: '/projects/:projectId/floors',
			params: projectIdParamsSchema,
			body: insertFloorsBodySchema,
			response: z.string(),
			operationId: 'insertFloors',
			tags: ['Floor'],
			summary: 'Insert floors into a project',
		}),
		uploadFloorImages: route({
			method: 'POST',
			path: '/projects/:projectId/floors/from-images',
			contentType: 'multipart/form-data',
			params: projectIdParamsSchema,
			body: uploadFloorImagesBodySchema,
			response: z.string(),
			operationId: 'uploadFloorImages',
			tags: ['Floor'],
			summary: 'Create the floors of a project from uploaded images',
		}),
		deleteFloors: route({
			method: 'DELETE',
			path: '/projects/:projectId/floors',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'deleteFloors',
			tags: ['Floor'],
			summary: 'Delete every floor of a project',
		}),
		deleteFloorsByFloorIds: route({
			method: 'DELETE',
			path: '/projects/:projectId/floors/byFloorIds',
			params: projectIdParamsSchema,
			body: deleteFloorsByIdsBodySchema,
			response: z.string(),
			operationId: 'deleteFloorsByFloorIds',
			tags: ['Floor'],
			summary: 'Delete floors of a project by their floorIds',
		}),
		getFloor: route({
			method: 'GET',
			path: '/projects/:projectId/floors/:floorId',
			params: projectFloorParamsSchema,
			response: configFloorSchema,
			operationId: 'getFloor',
			tags: ['Floor'],
			summary: 'Get a floor of a project by floorId',
		}),
		updateFloor: route({
			method: 'PUT',
			path: '/projects/:projectId/floors/:floorId',
			params: projectFloorParamsSchema,
			body: updateFloorBodySchema,
			response: z.string(),
			operationId: 'updateFloor',
			tags: ['Floor'],
			summary: 'Update a floor of a project',
		}),
	},
	{ root: true }
);
